/**
 * Which tracks move together.
 *
 * The overlay (T-404) already draws two tracks on one axis, but the user has to
 * guess which pair is worth looking at. This searches the pairs and reports the
 * ones that move together, so the app points instead of waiting to be asked.
 *
 * **Correlation is the easy part. Not overclaiming is the whole job**, and there
 * are three separate ways this could lie:
 *
 *   1. **It is a search, not a finding.** With eight tracks there are 84
 *      combinations to test, and the strongest one out of 84 is expected to look
 *      strong even in noise. So `considered` is reported alongside the results
 *      and the UI states it: three of eighty-four is a very different claim from
 *      three of three.
 *   2. **It is not causation, and the wording must never suggest it is.**
 *      `describePairing` says "move together", never "affects" or "leads to".
 *      The lag makes this tempting and it is still not licensed.
 *   3. **A handful of days can produce any coefficient at all.** Hence a
 *      minimum overlap, and an empty result is a perfectly ordinary answer.
 *
 * Only quantitative tracks take part. A marker has no magnitude and an unordered
 * enum has no order, so neither has anything a coefficient could be computed
 * from — the same reasoning that keeps them out of the overlay.
 */

import type { Entry } from '../model/entry.js'
import { shiftDayKey } from '../model/timestamp.js'
import type { Track } from '../model/track.js'
import { isQuantitative } from '../model/track.js'
import type { EpochRange } from '../ports/storage.js'
import { bucketSeries, buildSeries } from './series.js'

/**
 * Days of overlap below which nothing is reported.
 *
 * Ten is not a statistical threshold, it is a floor: a coefficient over four
 * shared days is arithmetic rather than evidence, and putting one on screen
 * would be the app's first outright misleading number.
 */
export const MINIMUM_OVERLAP_DAYS = 10

/**
 * How strong a relationship has to look before it is worth mentioning.
 *
 * Deliberately high. The cost of a missed pairing is that the user finds it
 * themselves with the overlay, which they can already do; the cost of a spurious
 * one is that they believe something untrue about their own life.
 */
export const MINIMUM_COEFFICIENT = 0.5

/** The lags searched, in days. `1` means "b on the day after a". */
export const LAGS = [0, 1] as const

export interface TrackPairing {
  readonly a: Track
  readonly b: Track
  /** Days `b` trails `a`. `0` is the same day. */
  readonly lagDays: number
  /** Pearson's r over the overlapping days, `-1` to `1`. */
  readonly coefficient: number
  readonly overlapDays: number
  readonly direction: 'together' | 'opposite'
}

export type RelationshipShortfall =
  /** Fewer than two tracks have a magnitude to compare. */
  | 'tooFewTracks'
  /** Pairs exist, but none share enough days. */
  | 'notEnoughOverlap'
  /** Enough days, nothing strong enough to report. */
  | 'nothingStrong'

export interface Relationships {
  /** Strongest first. Empty is an ordinary answer, not a failure. */
  readonly pairings: readonly TrackPairing[]
  /**
   * How many (pair, lag) combinations were examined.
   *
   * Reported so the UI can say "3 of 84". A ranked list with the denominator
   * hidden is the single most misleading way to present a correlation search.
   */
  readonly considered: number
  /** Present only when `pairings` is empty, and says which kind of empty. */
  readonly shortfall: RelationshipShortfall | null
}

export interface RelationshipOptions {
  readonly minimumOverlapDays?: number
  readonly minimumCoefficient?: number
  /** Cap on the list. The rest are still counted in `considered`. */
  readonly limit?: number
}

/** Pearson's r. `null` when either side does not vary at all. */
function correlation(xs: readonly number[], ys: readonly number[]): number | null {
  const n = xs.length
  if (n < 2) {
    return null
  }
  const meanX = xs.reduce((sum, x) => sum + x, 0) / n
  const meanY = ys.reduce((sum, y) => sum + y, 0) / n

  let covariance = 0
  let varianceX = 0
  let varianceY = 0
  for (let i = 0; i < n; i++) {
    const dx = (xs[i] ?? 0) - meanX
    const dy = (ys[i] ?? 0) - meanY
    covariance += dx * dy
    varianceX += dx * dx
    varianceY += dy * dy
  }

  /*
   * A flat track has no correlation with anything, and the formula would divide
   * by zero. `null` rather than `0`: "they do not move together" and "one of
   * them did not move" are different statements, and only the second is true.
   */
  if (varianceX === 0 || varianceY === 0) {
    return null
  }
  return covariance / Math.sqrt(varianceX * varianceY)
}

/** One number per day for a track: the mean of that day's readings. */
function dailyMeans(
  track: Track,
  entries: readonly Entry[],
  range: EpochRange,
): Map<string, number> {
  const series = buildSeries(track, entries, range)
  if (series.kind !== 'quantitative') {
    return new Map()
  }
  const bucketed = bucketSeries(series, { size: 'day', aggregate: 'mean' })
  if (bucketed.kind !== 'quantitative') {
    return new Map()
  }
  return new Map(bucketed.buckets.map((bucket) => [bucket.bucket.key, bucket.value]))
}

export function findRelationships(
  tracks: readonly Track[],
  entries: readonly Entry[],
  range: EpochRange,
  options: RelationshipOptions = {},
): Relationships {
  const minimumOverlap = options.minimumOverlapDays ?? MINIMUM_OVERLAP_DAYS
  const minimumCoefficient = options.minimumCoefficient ?? MINIMUM_COEFFICIENT

  const comparable = tracks.filter((track) => isQuantitative(track.kind))
  if (comparable.length < 2) {
    return { pairings: [], considered: 0, shortfall: 'tooFewTracks' }
  }

  const daily = new Map(
    comparable.map((track) => [track.id, dailyMeans(track, entries, range)] as const),
  )

  const found: TrackPairing[] = []
  let considered = 0
  let sawEnoughOverlap = false

  for (let i = 0; i < comparable.length; i++) {
    for (let j = i + 1; j < comparable.length; j++) {
      const a = comparable[i]!
      const b = comparable[j]!
      /*
       * Lag 0 once, lag 1 in **both** directions. "Sleep, then Focus tomorrow"
       * and "Focus, then Sleep tomorrow" are different claims, and a search that
       * only tried one order would silently favour whichever way the track list
       * happens to be sorted.
       */
      const ordered: readonly (readonly [Track, Track, number])[] = [
        [a, b, 0],
        [a, b, 1],
        [b, a, 1],
      ]

      for (const [first, second, lag] of ordered) {
        considered += 1
        const firstDays = daily.get(first.id)
        const secondDays = daily.get(second.id)
        if (!firstDays || !secondDays) {
          continue
        }

        const xs: number[] = []
        const ys: number[] = []
        for (const [key, value] of firstDays) {
          // `shiftDayKey`, not local millisecond arithmetic: stepping a day key by
          // 86,400,000ms drops one across a spring-forward and repeats one across a
          // fall-back, which is the bug that helper was extracted to stop repeating.
          const other = secondDays.get(lag === 0 ? key : shiftDayKey(key, lag))
          if (other === undefined) {
            continue
          }
          xs.push(value)
          ys.push(other)
        }

        if (xs.length < minimumOverlap) {
          continue
        }
        sawEnoughOverlap = true

        const coefficient = correlation(xs, ys)
        if (coefficient === null || Math.abs(coefficient) < minimumCoefficient) {
          continue
        }
        found.push({
          a: first,
          b: second,
          lagDays: lag,
          coefficient,
          overlapDays: xs.length,
          direction: coefficient > 0 ? 'together' : 'opposite',
        })
      }
    }
  }

  found.sort((one, other) => Math.abs(other.coefficient) - Math.abs(one.coefficient))

  /*
   * One entry per pair of tracks, keeping whichever lag fits best.
   *
   * Two tracks that move together on the same day almost always appear to move
   * together a day apart as well, so reporting every lag that clears the bar
   * lists the same finding two or three times with the names swapped. That reads
   * as more evidence than there is — and the lag is a detail of the best fit,
   * not a separate discovery.
   */
  const seen = new Set<string>()
  const best = found.filter((pairing) => {
    const key = [pairing.a.id, pairing.b.id].sort().join('|')
    if (seen.has(key)) {
      return false
    }
    seen.add(key)
    return true
  })

  const limited = options.limit === undefined ? best : best.slice(0, options.limit)

  return {
    pairings: limited,
    considered,
    shortfall:
      limited.length > 0 ? null : sawEnoughOverlap ? 'nothingStrong' : 'notEnoughOverlap',
  }
}

/**
 * One pairing as a sentence, worded here so nothing can restate it differently.
 *
 * "Move together", never "affects", "drives", "leads to" or "because". The lag
 * makes a causal reading tempting — this app has no basis for one, and a printed
 * report that implies one is a great deal worse than a chart nobody reads.
 */
export function describePairing(pairing: TrackPairing): string {
  const shape =
    pairing.direction === 'together'
      ? 'tend to be high together and low together'
      : 'tend to move in opposite directions'
  const when =
    pairing.lagDays === 0
      ? 'on the same day'
      : `with ${pairing.b.name} the day after ${pairing.a.name}`
  return `${pairing.a.name} and ${pairing.b.name} ${shape}, ${when}, across ${String(pairing.overlapDays)} days.`
}

/** Why the list is empty, in the user's terms. `null` when it is not. */
export function describeShortfall(relationships: Relationships): string | null {
  switch (relationships.shortfall) {
    case null:
      return null
    case 'tooFewTracks':
      return 'Comparing needs at least two tracks that record a number. A yes/no, an unordered set and a marker have nothing to correlate.'
    case 'notEnoughOverlap':
      return `No two tracks have been recorded on the same ${String(MINIMUM_OVERLAP_DAYS)} days yet. Recording a few of them together is what makes this possible.`
    case 'nothingStrong':
      return 'Nothing here moves together strongly enough to be worth pointing at, which is a perfectly ordinary answer.'
  }
}
